"use client"

/**
 * Outfame Navigation Component
 *
 * Fixed header with rich dropdown menus and mobile hamburger menu.
 * Matches Modash.io reference design.
 */
import { useEffect, useState, useCallback, useRef } from "react"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { useRouter } from "next/navigation"
import Image from "next/image"
import { T } from "gt-next"
import { enabledLocales } from "@/constants"
import { useDropdown, useMobileMenu } from "./hooks/useInteractivity"
import { NAV_ITEMS } from "./constants/navigation-landing"

// Outfame Logo
function OutfameLogo({ className = "" }: { className?: string }) {
    return (
        <svg width={120} height={24} viewBox="0 0 790 159" fill="none" xmlns="http://www.w3.org/2000/svg" className={`logo-hover-effect block ${className}`} role="img" aria-hidden="true">
            <rect width="157" height="159" fill="#FFA3D1" rx="33" />
            <path
                fill="#141414"
                d="M123.226 90c0 24.55-20.069 44.45-44.827 44.45S33.572 114.55 33.572 90c0-24.6 20.07-44.5 44.827-44.5 24.758 0 44.827 19.9 44.827 44.5Zm-20.018-.25c0-13.6-11.144-24.65-24.91-24.65-13.715 0-24.859 11.05-24.859 24.65 0 13.65 11.144 24.7 24.86 24.7 13.765 0 24.909-11.05 24.909-24.7Z"
            />
            <path
                fill="#141414"
                d="M35.186 52.75c-8.32-4.3-10.69-8.5-8.169-14.1 2.32-5 11.95-9.25 26.422-11.6 32.776-5.4 70.241.45 76.241 11.9 1.21 2.4.857 6.4-.807 8.4-2.067 2.55-6.504 5.5-10.084 6.7l-3.429 1.15-1.16-1c-1.916-1.7-7.866-5.7-8.421-5.7-1.412 0-.353-1 1.362-1.3 4.538-.75 12.253-3.6 12.606-4.6.1-.35-1.412-1.35-3.429-2.3-13.11-6.25-44.323-7.8-65.5-3.25-5.598 1.2-12.859 3.7-13.665 4.7-1.06 1.25 1.966 2.6 13.564 6.05.352.1-1.009 1.3-3.026 2.65-2.067 1.35-4.286 2.95-4.992 3.5-1.765 1.45-2.723 1.3-7.513-1.2Z"
            />
            <path
                fill="#141414"
                d="M295.581 80.65c0 24.108-19.606 43.65-43.791 43.65-24.184 0-43.79-19.542-43.79-43.65C208 56.543 227.606 37 251.79 37c24.185 0 43.791 19.543 43.791 43.65Zm-21.903-.265c0-12.14-9.872-21.98-22.051-21.98-12.178 0-22.05 9.84-22.05 21.98s9.872 21.98 22.05 21.98c12.179 0 22.051-9.84 22.051-21.98ZM333.854 122.589c-14.708-2.333-24.887-11.414-28.5-25.416-.763-2.891-.865-6.747-.967-30.285l-.051-27.04h22.647V64.96c0 15.777.204 25.974.56 27.597.815 3.855 2.952 6.747 6.362 8.726 2.799 1.623 3.308 1.724 7.583 1.724 5.344 0 7.532-.913 10.586-4.26 3.562-3.958 3.613-4.313 3.817-33.128l.152-25.77h22.342v25.263c0 14.002-.254 26.785-.509 28.764-.712 4.971-2.799 10.856-5.038 14.306-3.97 6.138-12.113 11.718-19.899 13.595-3.766.914-15.268 1.421-19.085.812ZM755.756 122.488c-12.622-1.167-21.935-5.327-26.566-12.023-4.173-5.834-5.344-13.088-3.207-19.43 1.425-4.16 3.156-6.595 6.413-9.131 1.527-1.167 2.748-2.232 2.748-2.334 0-.152-1.272-1.674-2.799-3.348-3.97-4.413-5.038-7.406-5.089-13.798 0-4.667.152-5.378 1.577-8.422 5.039-10.551 15.268-15.168 33.539-15.168 10.382 0 23.92 1.37 25.497 2.587.356.305.306 2.03-.102 5.885-.356 2.993-.661 6.646-.712 8.016-.051 3.5-.356 3.855-2.85 3.297-4.479-1.015-14.963-2.03-20.815-2.03-5.751 0-6.362.102-8.907 1.421-3.257 1.623-4.58 3.247-4.885 5.986-.306 2.283.509 4.008 2.494 5.48 1.374 1.014 2.239 1.064 14.708 1.318l13.232.254v8.624l-.051 8.624-13.639.254c-15.574.304-15.726.355-17.559 4.413-1.832 4.11-.05 7.863 4.581 9.639 3.715 1.42 18.27 1.268 27.991-.305 4.02-.608 7.379-1.065 7.481-.964.051.051.204 4.262.306 9.233l.203 9.132-2.036.608c-6.005 1.725-23.461 2.943-31.553 2.182ZM408.412 91.137c0-16.741-.153-30.743-.305-31.2-.255-.71-1.782-.811-11.706-.811h-11.4V39.849h33.793c18.576 0 34.098.101 34.506.253.508.203.661 2.283.661 9.538v9.232l-11.502.153-11.552.101-.255 1.928c-.203 1.014-.305 15.016-.356 31.046v29.17l-10.942.152-10.942.102V91.137ZM459.814 93.47c0-26.075.051-28.358.967-31.909 3.919-15.32 15.319-22.727 34.861-22.727 6.871 0 16.999.761 21.07 1.573l2.646.507-.254 5.175c-.153 2.84-.407 6.848-.56 8.928l-.305 3.703-2.341-.253c-1.323-.203-6.158-.609-10.79-.913-6.616-.508-9.16-.508-12.061 0-7.431 1.268-10.484 4.92-10.535 12.48l-.051 4.057 14.25.305c7.838.203 14.403.355 14.657.406.204 0 .306 4.109.255 9.08l-.153 8.98-13.995.101c-7.685 0-14.301.102-14.607.102-.509.05-.661 3.246-.661 14.255v14.204h-22.393V93.47ZM523.633 94.79l.255-26.786 1.272-4.21c.713-2.284 2.036-5.632 2.952-7.357 2.087-3.957 8.652-10.551 13.13-13.19 12.673-7.355 31.859-6.29 43.87 2.436 5.089 3.703 10.128 11.617 12.214 19.277 1.069 3.906 1.12 5.124 1.324 30.337l.152 26.227h-22.444v-9.588c0-7.052-.152-9.689-.61-10.045-.407-.253-6.871-.304-15.166-.152l-14.505.254-.102 9.639-.101 9.638-11.248.152-11.247.102.254-26.735Zm52.318-17.909c-.101-3.195-.509-7-.916-8.42-1.069-3.958-3.613-7.306-6.87-9.081-2.494-1.37-3.207-1.522-7.329-1.522-4.071 0-4.835.152-6.87 1.37-3.156 1.826-4.784 3.703-6.413 7.203-1.17 2.587-1.425 3.906-1.628 8.98-.153 3.297-.102 6.34.05 6.746.306.761 2.036.812 15.217.71l14.912-.151-.153-5.834ZM609.541 88.702c.203-32.569.203-32.924 1.323-35.917 2.392-6.443 8.143-11.719 14.453-13.19 10.128-2.384 19.238 1.116 23.767 9.08.815 1.421 2.647 6.494 4.072 11.212 1.476 4.769 3.511 11.465 4.631 14.966 1.069 3.5 2.596 8.37 3.308 10.805.763 2.587 1.578 4.413 1.934 4.261.662-.203.356.66 4.886-15.32 7.277-25.619 7.735-26.684 12.01-30.641 4.123-3.703 7.38-4.87 13.996-4.87 6.26 0 9.212.862 12.927 3.703 4.02 2.993 6.718 6.697 8.041 10.907 1.119 3.602 1.119 3.957 1.17 35.613v31.959h-22.698l.051-29.474c0-16.183-.102-29.575-.305-29.727-.916-.914-1.934.811-3.563 6.138a1045.086 1045.086 0 0 1-3.511 10.958 1001.17 1001.17 0 0 0-3.817 11.921c-5.7 18.364-6.209 19.734-8.805 22.626-2.595 2.993-5.903 4.261-11.044 4.261-3.969 0-4.58-.152-7.379-1.674-2.341-1.319-3.41-2.334-4.682-4.363-1.374-2.333-4.479-11.161-9.873-28.206-4.733-15.067-6.82-20.799-7.685-20.951-.865-.152-.916 1.522-.916 29.271v29.474h-22.444l.153-32.822Z"
            />
        </svg>
    )
}

// Chevron Down Icon
function ChevronDownIcon({ isOpen = false }: { isOpen?: boolean }) {
    return (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" className="ml-0.5 opacity-100 transition-transform duration-200 ease-out" style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}>
            <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    )
}

// Mobile Accordion Component - Premium, snappy animations
function MobileAccordion({ title, isOpen, onToggle, children }: { title: string; isOpen: boolean; onToggle: () => void; children: React.ReactNode }) {
    const contentRef = useRef<HTMLDivElement>(null)
    const [height, setHeight] = useState(0)

    useEffect(() => {
        if (contentRef.current) {
            setHeight(contentRef.current.scrollHeight)
        }
    }, [children])

    return (
        <div className="mobile-accordion border-b border-ink/[0.06]">
            <button
                className="flex w-full items-center justify-between py-5 text-[22px] font-semibold text-ink active:opacity-70"
                onClick={onToggle}
                aria-expanded={isOpen}
            >
                <span>{title}</span>
                <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    className="text-ink/30"
                    style={{
                        transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                        transition: "transform 200ms cubic-bezier(0.22, 1, 0.36, 1)"
                    }}
                >
                    <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </button>
            <div
                ref={contentRef}
                className="overflow-hidden"
                style={{
                    maxHeight: isOpen ? `${height}px` : "0",
                    opacity: isOpen ? 1 : 0,
                    transition: "max-height 200ms cubic-bezier(0.22, 1, 0.36, 1), opacity 150ms ease-out"
                }}
            >
                <div className="flex flex-col pb-3">{children}</div>
            </div>
        </div>
    )
}

export default function Navigation() {
    const dropdown = useDropdown()
    const mobile = useMobileMenu()
    const pathname = usePathname()
    const router = useRouter()

    // Extract current locale from pathname for locale-aware navigation
    // usePathname() returns the middleware-rewritten path (/ -> /en), so treat
    // "en" as no-locale -> the logo links to "/" on English pages (not /en, which 301s).
    const pathSegments = pathname.split("/").filter(Boolean)
    const currentLocale = enabledLocales.includes(pathSegments[0]) && pathSegments[0] !== "en" ? pathSegments[0] : null

    // Track which routes have been prefetched to avoid duplicate calls
    const prefetchedRoutes = useRef<Set<string>>(new Set())

    // Hover-based prefetch for Safari/Firefox (no Speculation Rules support)
    // Chrome uses Speculation Rules, others fall back to Next.js router.prefetch
    const handlePrefetchOnHover = useCallback((route: string) => {
        if (prefetchedRoutes.current.has(route)) return
        prefetchedRoutes.current.add(route)
        router.prefetch(route)
    }, [router])

    // Auth state for showing Dashboard vs Login buttons
    const [isLoggedIn, setIsLoggedIn] = useState(false)
    useEffect(() => {
        const cookies = Object.fromEntries(document.cookie.split("; ").map((cookie) => cookie.split("=")))
        const supabaseAuthCookie = Object.keys(cookies).find((name) => /^sb-.+-auth-token$/.test(name))
        setIsLoggedIn(!!supabaseAuthCookie)
    }, [])

    // Handle logo click - scroll to top if on homepage
    const handleLogoClick = (e: React.MouseEvent) => {
        // Check if on homepage (root path or locale-prefixed root like /en, /es, etc.)
        const isHomepage = pathname === "/" || /^\/[a-z]{2}$/.test(pathname)
        if (isHomepage) {
            e.preventDefault()
            window.scrollTo({ top: 0, behavior: "smooth" })
        }
    }

    // Hide navigation on /start page (uses its own MinimalNav)
    const isStartPage = pathname === "/start" || pathname?.match(/^\/[a-z]{2}\/start$/)
    if (isStartPage) return null

    return (
        <>
            {/* Fixed Navigation Container */}
            <div className="z-2000 fixed inset-x-0 flex items-center justify-center" style={{ top: "calc(48px + var(--smart-banner-h, 0px))", padding: "28px clamp(16px, 5vw, 28px) 0", transition: "top 300ms cubic-bezier(0.22, 1, 0.36, 1)" }}>
                <nav
                    className="flex w-full max-w-[1136px] items-center justify-between bg-white"
                    style={{
                        borderRadius: "16px",
                        height: "56px",
                        boxShadow: "0 2px 3px rgba(0,0,0,0.04), 0 0 0 1px rgba(0,0,0,0.1)"
                    }}
                    role="navigation"
                    aria-label="Main navigation">
                    {/* Logo */}
                    <Link href={currentLocale ? `/${currentLocale}` : "/"} className="shrink-0" style={{ paddingLeft: "0", marginLeft: "16px" }} aria-label="Outfame - Home" onClick={handleLogoClick}>
                        <OutfameLogo />
                    </Link>

                    {/* Desktop Navigation */}
                    <div className="hidden items-center lg:flex" style={{ gap: "8px", paddingLeft: "40px" }}>
                        {/* Platform Dropdown */}
                        <div className="group relative">
                            <button
                                className="flex items-center rounded-lg text-[14px] font-semibold text-ink hover:bg-fill"
                                style={{ padding: "6px 12px" }}
                                onClick={() => dropdown.handleTriggerClick("platform")}
                                onMouseEnter={() => dropdown.handleTriggerEnter("platform")}
                                onMouseLeave={dropdown.handleTriggerLeave}
                                aria-expanded={dropdown.isOpen("platform")}
                                aria-haspopup="true">
                                <T>Platform</T>
                                <ChevronDownIcon isOpen={dropdown.isOpen("platform")} />
                            </button>

                            {/* Platform Dropdown Panel */}
                            <div className={`dropdown-panel ${dropdown.isOpen("platform") ? "active" : ""}`} onMouseEnter={dropdown.handlePanelEnter} onMouseLeave={dropdown.handlePanelLeave}>
                                {dropdown.isOpen("platform") && (
                                <div className="overflow-hidden rounded-2xl bg-white" style={{ boxShadow: "0 12px 16px -4px rgba(0,0,0,0.08), 0 4px 6px -2px rgba(0,0,0,0.03)" }}>
                                    <div className="flex">
                                        {/* Left: Main items */}
                                        <div className="flex flex-col p-4">
                                            {/* Main 4 columns */}
                                            <div className="grid grid-cols-4 gap-4">
                                                {NAV_ITEMS.platform.main.map((item) => (
                                                    <Link key={item.title} href={item.href} prefetch={false} className="flex flex-col gap-3 hover:opacity-70" onClick={dropdown.close}>
                                                        <div className="relative h-[150px] w-full overflow-hidden rounded-lg bg-gray-100">
                                                            <Image src={item.image} alt={item.title} fill sizes="205px" loading="lazy" className="object-cover" />
                                                        </div>
                                                        <div className="flex flex-col gap-2">
                                                            <span className="text-[16px] font-semibold leading-[150%] text-ink">{item.title}</span>
                                                            <span className="text-[14px] font-normal leading-[140%] text-[rgba(20,20,20,0.5)]">{item.description}</span>
                                                        </div>
                                                    </Link>
                                                ))}
                                            </div>
                                        </div>
                                        {/* Right sidebar */}
                                        <div
                                            className="flex flex-col gap-6 p-4"
                                            style={{
                                                backgroundColor: "rgba(0,0,0,0.02)",
                                                borderLeft: "1px solid rgba(0,0,0,0.08)",
                                                minWidth: "220px"
                                            }}>
                                            <div className="flex flex-col gap-4">
                                                {NAV_ITEMS.platform.integrations.map((item) => (
                                                    <Link key={item.title} href={item.href} prefetch={false} className="flex flex-col gap-1 hover:opacity-70" onClick={dropdown.close}>
                                                        <div className="flex items-center gap-2">
                                                            <Image src={item.icon} alt="" width={20} height={20} loading="lazy" />
                                                            <span className="text-[14px] font-semibold text-ink">{item.title}</span>
                                                        </div>
                                                        <span className="text-[14px] font-normal leading-[140%] text-[rgba(20,20,20,0.5)]">{item.description}</span>
                                                    </Link>
                                                ))}
                                            </div>
                                            <div className="flex flex-col gap-3">
                                                {NAV_ITEMS.platform.more.map((item) => (
                                                    <Link key={item.title} href={item.href} prefetch={false} className="flex items-center gap-2 hover:opacity-70" onClick={dropdown.close}>
                                                        <Image src={item.icon} alt="" width={20} height={20} loading="lazy" />
                                                        <span className="text-[14px] font-semibold text-ink">{item.title}</span>
                                                    </Link>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                )}
                            </div>
                        </div>

                        <Link href="/pricing" prefetch={false} onMouseEnter={() => handlePrefetchOnHover("/pricing")} className="rounded-lg text-[14px] font-semibold text-ink hover:bg-fill" style={{ padding: "6px 12px" }}>
                            <T>Pricing</T>
                        </Link>

                        <Link href="/outfame-reviews" prefetch={false} onMouseEnter={() => handlePrefetchOnHover("/outfame-reviews")} className="rounded-lg text-[14px] font-semibold text-ink hover:bg-fill" style={{ padding: "6px 12px" }}>
                            <T>Real Reviews</T>
                        </Link>

                        <Link href="/results" prefetch={false} className="rounded-lg text-[14px] font-semibold text-ink hover:bg-fill" style={{ padding: "6px 12px" }}>
                            <T>Results</T>
                        </Link>

                        {/* Resources Dropdown */}
                        <div className="group relative">
                            <button
                                className="flex items-center rounded-lg text-[14px] font-semibold text-ink hover:bg-fill"
                                style={{ padding: "6px 12px" }}
                                onClick={() => dropdown.handleTriggerClick("resources")}
                                onMouseEnter={() => dropdown.handleTriggerEnter("resources")}
                                onMouseLeave={dropdown.handleTriggerLeave}
                                aria-expanded={dropdown.isOpen("resources")}
                                aria-haspopup="true">
                                <T>Resources</T>
                                <ChevronDownIcon isOpen={dropdown.isOpen("resources")} />
                            </button>

                            <div className={`dropdown-panel ${dropdown.isOpen("resources") ? "active" : ""}`} onMouseEnter={dropdown.handlePanelEnter} onMouseLeave={dropdown.handlePanelLeave}>
                                {dropdown.isOpen("resources") && (
                                <div className="overflow-hidden rounded-2xl bg-white" style={{ boxShadow: "0 12px 16px -4px rgba(0,0,0,0.08), 0 4px 6px -2px rgba(0,0,0,0.03)" }}>
                                    <div className="flex">
                                        <div className="grid grid-cols-4 gap-4 p-4">
                                            {NAV_ITEMS.resources.main.map((item) => (
                                                <Link key={item.title} href={item.href} prefetch={false} className="flex flex-col gap-3 hover:opacity-70" onClick={dropdown.close}>
                                                    <div className="relative h-[150px] w-full overflow-hidden rounded-lg bg-gray-100">
                                                            <Image src={item.image} alt={item.title} fill sizes="205px" loading="lazy" className="object-cover" />
                                                        </div>
                                                    <div className="flex flex-col gap-2">
                                                        <span className="text-[16px] font-semibold leading-[150%] text-ink">{item.title}</span>
                                                        <span className="text-[14px] font-normal leading-[140%] text-[rgba(20,20,20,0.5)]">{item.description}</span>
                                                    </div>
                                                </Link>
                                            ))}
                                        </div>
                                        <div
                                            className="flex flex-col gap-3 p-4"
                                            style={{
                                                backgroundColor: "rgba(0,0,0,0.02)",
                                                borderLeft: "1px solid rgba(0,0,0,0.08)",
                                                minWidth: "180px"
                                            }}>
                                            <span className="text-[12px] font-normal leading-[130%] text-[rgba(20,20,20,0.5)]"><T>More</T></span>
                                            {NAV_ITEMS.resources.more.map((item) => (
                                                item.href.startsWith("http") ? (
                                                    <a key={item.title} href={item.href} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:opacity-70" onClick={dropdown.close}>
                                                        <Image src={item.icon} alt="" width={20} height={20} loading="lazy" />
                                                        <span className="text-[14px] font-semibold text-ink">{item.title}</span>
                                                    </a>
                                                ) : (
                                                <Link key={item.title} href={item.href} prefetch={false} className="flex items-center gap-2 hover:opacity-70" onClick={dropdown.close}>
                                                    <Image src={item.icon} alt="" width={20} height={20} loading="lazy" />
                                                    <span className="text-[14px] font-semibold text-ink">{item.title}</span>
                                                </Link>
                                                )
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Desktop CTA Buttons */}
                    <div className="hidden items-center lg:flex" style={{ gap: "8px", marginRight: "16px" }}>
                        {isLoggedIn ? (
                            <Link href="/app" className="inline-flex items-center justify-center rounded-lg bg-ink text-[14px] font-semibold text-white hover:opacity-90" style={{ padding: "6px 12px" }}>
                                <T>Go to Dashboard</T>
                            </Link>
                        ) : (
                            <>
                                <Link href="/login" prefetch={false} className="rounded-lg text-[14px] font-semibold text-ink hover:bg-fill" style={{ padding: "6px 12px" }}>
                                    <T>Log in</T>
                                </Link>
                                <Link href="/setup" prefetch={true} className="inline-flex items-center justify-center rounded-lg bg-ink text-[14px] font-semibold text-white hover:opacity-90" style={{ padding: "6px 12px" }}>
                                    <T>Start growing</T>
                                </Link>
                            </>
                        )}
                    </div>

                    {/* Mobile CTA + Menu Button */}
                    <div className="flex items-center lg:hidden" style={{ gap: "8px", marginRight: "16px" }}>
                        <button className="flex h-12 w-12 flex-col items-center justify-center transition-transform active:scale-[0.98]" onClick={mobile.toggle} aria-label="Toggle menu" aria-expanded={mobile.isOpen}>
                            <span className="mobile-menu-icon flex flex-col items-center justify-center gap-[6px]">
                                <span className="block h-[2px] w-5 origin-center rounded-full bg-ink transition-transform duration-300" style={{ transform: mobile.isOpen ? "translateY(4px) rotate(45deg)" : "none" }} />
                                <span className="block h-[2px] w-5 origin-center rounded-full bg-ink transition-transform duration-300" style={{ transform: mobile.isOpen ? "translateY(-4px) rotate(-45deg)" : "none" }} />
                            </span>
                        </button>
                    </div>
                </nav>
            </div>

            {/* Page Overlay */}
            <div ref={dropdown.overlayRef} className="page-overlay pointer-events-none fixed inset-0 bg-black/0 transition-[background-color,backdrop-filter,-webkit-backdrop-filter] duration-300 ease-out" style={{ zIndex: 1000, backdropFilter: "blur(0px)", WebkitBackdropFilter: "blur(0px)" }} />

            {/* Mobile Menu - Full Screen Overlay */}
            <div
                ref={mobile.menuRef}
                className="fixed inset-0 lg:hidden"
                style={{
                    zIndex: 1999,
                    pointerEvents: mobile.isOpen ? "auto" : "none"
                }}
                aria-hidden={!mobile.isOpen}
            >
                {/* Backdrop with blur - covers entire viewport including header */}
                <div
                    className="absolute inset-0"
                    style={{
                        backgroundColor: mobile.isOpen ? "rgba(255, 255, 255, 0.92)" : "rgba(255, 255, 255, 0)",
                        backdropFilter: mobile.isOpen ? "blur(12px)" : "blur(0px)",
                        WebkitBackdropFilter: mobile.isOpen ? "blur(12px)" : "blur(0px)",
                        // Open: 180ms fast blur establishment | Close: 220ms lingers after content
                        transition: mobile.isOpen
                            ? "background-color 180ms ease-out, backdrop-filter 180ms ease-out, -webkit-backdrop-filter 180ms ease-out"
                            : "background-color 220ms ease-out, backdrop-filter 220ms ease-out, -webkit-backdrop-filter 220ms ease-out"
                    }}
                    onClick={mobile.close}
                />

                {/* Menu Content Container - starts below header + sale banner */}
                <div
                    className="relative flex flex-col"
                    style={{
                        height: "calc(100% - 132px - var(--smart-banner-h, 0px))",
                        marginTop: "calc(132px + var(--smart-banner-h, 0px))",
                        opacity: mobile.isOpen ? 1 : 0,
                        transform: mobile.isOpen ? "scale(1)" : "scale(0.98)",
                        // Open: 280ms smooth reveal | Close: 150ms fast dismissal (finishes before backdrop)
                        transition: mobile.isOpen
                            ? "opacity 280ms cubic-bezier(0.22, 1, 0.36, 1), transform 280ms cubic-bezier(0.22, 1, 0.36, 1)"
                            : "opacity 150ms ease-out, transform 150ms ease-out"
                    }}
                >
                    {/* Top Scroll Shadow */}
                    <div
                        className="pointer-events-none absolute left-0 right-0 z-10 h-6"
                        style={{
                            top: 0,
                            background: "linear-gradient(to bottom, rgba(255,255,255,0.95) 0%, transparent 100%)",
                            opacity: mobile.canScrollTop ? 1 : 0,
                            transition: "opacity 200ms ease-out"
                        }}
                    />

                    {/* Scrollable Content Area */}
                    <div
                        ref={mobile.scrollRef}
                        className="flex-1 overflow-y-auto overscroll-contain px-6"
                        onScroll={mobile.checkScroll}
                        style={{ paddingTop: "12px", paddingBottom: "16px" }}
                    >
                        {/* Platform Accordion */}
                        <MobileAccordion title="Platform" isOpen={mobile.isAccordionOpen("platform")} onToggle={() => mobile.toggleAccordion("platform")}>
                            {NAV_ITEMS.platform.main.map((item) => (
                                <Link
                                    key={item.title}
                                    href={item.href}
                                    prefetch={false}
                                    className="-mx-2 flex items-center justify-between rounded-xl px-2 py-3 text-[17px] text-ink/80 transition-colors active:bg-ink/[0.04] active:text-ink"
                                    onClick={mobile.closeForNavigation}
                                >
                                    <span>{item.title}</span>
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="text-ink/25">
                                        <path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </Link>
                            ))}
                        </MobileAccordion>

                        <Link
                            href="/pricing"
                            prefetch={false}
                            onTouchStart={() => handlePrefetchOnHover("/pricing")}
                            className="flex items-center border-b border-ink/[0.06] py-5 text-[22px] font-semibold text-ink active:opacity-70"
                            onClick={mobile.closeForNavigation}
                        >
                            <T>Pricing</T>
                        </Link>

                        <Link
                            href="/outfame-reviews"
                            prefetch={false}
                            onTouchStart={() => handlePrefetchOnHover("/outfame-reviews")}
                            className="flex items-center border-b border-ink/[0.06] py-5 text-[22px] font-semibold text-ink active:opacity-70"
                            onClick={mobile.closeForNavigation}
                        >
                            <T>Real Reviews</T>
                        </Link>

                        <Link
                            href="/results"
                            prefetch={false}
                            className="flex items-center border-b border-ink/[0.06] py-5 text-[22px] font-semibold text-ink active:opacity-70"
                            onClick={mobile.closeForNavigation}
                        >
                            <T>Results</T>
                        </Link>

                        {/* Resources Accordion */}
                        <MobileAccordion title="Resources" isOpen={mobile.isAccordionOpen("resources")} onToggle={() => mobile.toggleAccordion("resources")}>
                            {NAV_ITEMS.resources.main.map((item) => (
                                <Link
                                    key={item.title}
                                    href={item.href}
                                    prefetch={false}
                                    className="-mx-2 flex items-center justify-between rounded-xl px-2 py-3 text-[17px] text-ink/80 transition-colors active:bg-ink/[0.04] active:text-ink"
                                    onClick={mobile.closeForNavigation}
                                >
                                    <span>{item.title}</span>
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="text-ink/25">
                                        <path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </Link>
                            ))}
                        </MobileAccordion>
                    </div>

                    {/* Bottom Scroll Shadow - positioned above CTA footer (pt-3 + h-56px + pb-8 = 100px) */}
                    <div
                        className="pointer-events-none absolute left-0 right-0 z-10 h-8"
                        style={{
                            bottom: "100px",
                            background: "linear-gradient(to top, rgba(255,255,255,0.95) 0%, transparent 100%)",
                            opacity: mobile.canScrollBottom ? 1 : 0,
                            transition: "opacity 200ms ease-out"
                        }}
                    />

                    {/* Sticky CTA Footer - transparent background */}
                    <div className="shrink-0 px-6 pt-3 pb-8">
                        {isLoggedIn ? (
                            <Link
                                href="/app"
                                className="flex h-[56px] w-full items-center justify-center rounded-2xl bg-ink text-[17px] font-semibold text-white transition-transform active:scale-[0.98]"
                                onClick={mobile.closeForNavigation}
                            >
                                <T>Go to Dashboard</T>
                            </Link>
                        ) : (
                            <div className="flex gap-3">
                                <Link
                                    href="/login"
                                    prefetch={false}
                                    className="flex h-[56px] flex-1 items-center justify-center rounded-2xl border border-ink/10 bg-white/50 text-[17px] font-semibold text-ink backdrop-blur-sm transition-colors active:bg-ink/5"
                                    onClick={mobile.closeForNavigation}
                                >
                                    <T>Log in</T>
                                </Link>
                                <Link
                                    href="/setup"
                                    prefetch={true}
                                    className="flex h-[56px] flex-1 items-center justify-center rounded-2xl bg-ink text-[17px] font-semibold text-white transition-transform active:scale-[0.98]"
                                    onClick={mobile.closeForNavigation}
                                >
                                    <T>Start growing</T>
                                </Link>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    )
}
